import { propertyTypeDisplayNames } from "@/lib/data/propertyTypes";
import { Listing } from "@/lib/types/listingTypes";
import { PropertyType } from "@/lib/types/propertyTypes";

/**
 * Converts a property type ID to its display name using a property type map
 */
export const getPropertyTypeName = (
  propertyTypeId: number | undefined,
  propertyTypes: PropertyType[]
): string => {
  if (!propertyTypeId) return "Unknown";

  const propertyType = propertyTypes.find((type) => type.id === propertyTypeId);

  return propertyType?.name || `Type ${propertyTypeId}`;
};

/**
 * Gets a human-friendly display name for a property type
 */
export const getPropertyTypeDisplayName = (
  propertyType: string | undefined
): string => {
  if (!propertyType) return "Property";

  return propertyTypeDisplayNames[propertyType] || propertyType;
};

/**
 * Converts a canonical Listing object (from listingTypes.ts)
 * to a canonical PropertyType object (from propertyTypes.ts).
 * Maps relevant fields.
 */
export const convertListingToPropertyType = (
  listing: Listing // Use canonical Listing
): PropertyType => {
  // Return canonical PropertyType

  // Construct the canonical PropertyType object
  const propertyType: PropertyType = {
    // Map fields from Listing to PropertyType
    id: Number(listing.property_type_id ?? listing.id ?? 0), // Prioritize property_type_id, fallback to listing id
    name: listing.propertyType?.name ?? "Unknown Type", // Use nested propertyType name if available
    description: listing.description || "", // Map description
    created_at: listing.created_at || new Date().toISOString(), // Map created_at
    updated_at: listing.updated_at || new Date().toISOString(), // Map updated_at

    // Map optional short codes if they exist on the Listing type (check definition)
    // short_code_rent: listing.short_code_rent, // If listing has these...
    // short_code_sale: listing.short_code_sale,
  };

  // Clean up potentially undefined optional fields (like short codes if they weren't present)
  Object.keys(propertyType).forEach((key) => {
    const typedKey = key as keyof PropertyType;
    if (propertyType[typedKey] === undefined) {
      delete propertyType[typedKey];
    }
  });

  return propertyType;
};

/**
 * Converts a canonical PropertyType object (from propertyTypes.ts)
 * to a canonical Listing object (from listingTypes.ts).
 * Provides defaults for fields missing in PropertyType.
 */
export const convertPropertyTypeToListing = (
  property: PropertyType // Input: Canonical PropertyType
): Listing => {
  // Output: Canonical Listing

  // Construct the canonical Listing object
  const listing: Partial<Listing> = {
    // Start with Partial<Listing>
    // Fields directly from PropertyType
    id: property.id,
    description: property.description || "",
    created_at: property.created_at || new Date().toISOString(),
    updated_at: property.updated_at || new Date().toISOString(),
    property_type_id: property.id, // Map property_type_id from PropertyType's id

    // Fields requiring mapping or defaults for Listing
    title: property.name || "Untitled Listing",
    price: 0,
    currency: "LKR",
    size: 0,
    size_unit: "sqft",
    address: "", // PropertyType doesn't define address
    location_id: 0,
    latitude: 0,
    longitude: 0,
    rooms: 0,
    bathrooms: 0,
    parking_spaces: 0,

    images: [],

    listing_type: "rent",
    status: "active",

    is_featured: false,
    is_boosted: false,

    views: 0,
    user_id: 0,

    // Nested objects - provide defaults conforming to Listing type
    // Assuming propertyType on Listing is just an object with id/name
    propertyType: { id: property.id, name: property.name },
    // Assuming location on Listing is just an object with id/name
    location: { id: 0, name: "Unknown Location" },

    // Assume features/amenities are not part of PropertyType
    // features: [], // Not defined in canonical Listing type from listingTypes.ts?
    // amenities: [], // Not defined in canonical Listing type from listingTypes.ts?
  };

  // Ensure all required fields are present, even if just defaults
  const requiredDefaults: Pick<
    Listing,
    "title" | "price" | "size" | "created_at" | "updated_at" | "status"
  > = {
    title: listing.title!, // Already defaulted
    price: listing.price!, // Already defaulted
    size: listing.size!, // Already defaulted
    created_at: listing.created_at!, // Already defaulted
    updated_at: listing.updated_at!, // Already defaulted
    status: listing.status!, // Already defaulted
  };

  // Combine and cast to Listing - ensure all required fields are covered
  return { ...requiredDefaults, ...listing } as Listing;
};

/**
 * Helper function to get address parts consistently from Listing.
 * Canonical PropertyType does not contain address info.
 */
export const getPropertyAddressParts = (property: Listing | PropertyType) => {
  let addressString = "";

  // Check if it's a Listing (assuming Listing always has a title)
  if ("title" in property && property.title) {
    const listing = property as Listing;
    if (typeof listing.address === "string") {
      addressString = listing.address;
    } else if (
      listing.location &&
      typeof listing.location === "object" &&
      listing.location.name
    ) {
      addressString = listing.location.name; // Use location name if address string is missing
    }
  }
  // If it's a PropertyType, it has no address field according to canonical type
  // else {
  //   // Handle PropertyType case if needed, maybe return default parts
  // }

  // Assuming getAddressParts was meant to split the address string
  return addressString
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
};
